/**
 * Chalkboard mode resolution + React context.
 *
 * Spec: docs/design/chalkboard.md (section 2 — Mode selection)
 * Mode selection has two layers:
 *   1. Route-driven fixed mode — marketing pages render light ("paper"),
 *      host flows and the live exercise render dark ("chalkboard").
 *   2. User preference — participant browsing surfaces (dashboard, reports,
 *      study pages, journeys) default to LIGHT and expose a toggle; the
 *      choice persists on the device (localStorage).
 *
 * The resolved mode is also mirrored as a `dark` class on <html> so that
 * Tailwind `dark:` utilities (see @custom-variant in index.css) follow it.
 * Shared components styled light-first with `dark:` variants therefore render
 * correctly on both host (dark) and participant (toggleable) surfaces.
 *
 * Components in `components/chalkboard/` and `components/paper/` consume the
 * current mode via `useChalkboardMode()`. They also accept a `mode?` prop
 * override so an embedded widget on a light page can render dark internally
 * (e.g. live demo on the homepage).
 *
 * Wiring:
 *   <BrowserRouter>
 *     <ChalkboardModeAutoProvider>   {/ * reads useLocation, sets mode * /}
 *       <Routes>...</Routes>
 *     </ChalkboardModeAutoProvider>
 *   </BrowserRouter>
 */

import { createContext, useContext, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import type { ChalkboardMode } from './chalkboardTokens';

export type { ChalkboardMode };

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ChalkboardModeContext = createContext<ChalkboardMode>('dark');

export interface ChalkboardModeProviderProps {
  mode: ChalkboardMode;
  children: ReactNode;
}

/** Manual provider — pass an explicit mode. Use for previews or storybook. */
export function ChalkboardModeProvider({ mode, children }: ChalkboardModeProviderProps) {
  return <ChalkboardModeContext.Provider value={mode}>{children}</ChalkboardModeContext.Provider>;
}

/** Read the active chalkboard mode for the current subtree. */
export function useChalkboardMode(): ChalkboardMode {
  return useContext(ChalkboardModeContext);
}

// ---------------------------------------------------------------------------
// User theme preference (participant browsing surfaces)
// ---------------------------------------------------------------------------

const THEME_PREFERENCE_STORAGE_KEY = 'aot-theme-preference';

/**
 * Routes where the user's stored theme preference applies (participant
 * browsing surfaces). Default is LIGHT; a toggle lets the user switch.
 * The live exercise flow (/participant, /session, /join) intentionally stays
 * route-fixed dark — it is an immersive surface.
 *
 * A route may only be added here once its page tree is converted to
 * light-first classes with `dark:` variants — otherwise it renders as a
 * mixed light/dark page. Pending conversion: '/journey', '/programmes'
 * (cadence journey tree still renders through a fixed-dark layout).
 *
 */
const PREFERENCE_MODE_PREFIXES: readonly string[] = [
  '/dashboard',
  '/summary',
  '/sessions',
  '/study',
  '/public-research',
  '/signin',
  '/join/study',
];

/** True when the pathname is a participant surface with a theme toggle. */
export function isPreferenceThemedPath(pathname: string): boolean {
  return PREFERENCE_MODE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

function readStoredPreference(): ChalkboardMode {
  try {
    const raw = window.localStorage.getItem(THEME_PREFERENCE_STORAGE_KEY);
    return raw === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

interface ThemePreferenceValue {
  /** The stored preference (only meaningful where canToggle is true). */
  preference: ChalkboardMode;
  /** Whether the current route honors the user preference. */
  canToggle: boolean;
  /** Persist + apply a new preference. */
  setPreference: (mode: ChalkboardMode) => void;
}

const ThemePreferenceContext = createContext<ThemePreferenceValue>({
  preference: 'light',
  canToggle: false,
  setPreference: () => undefined,
});

/** Theme preference state for toggle UIs (see components/ThemeToggle.tsx). */
export function useThemePreference(): ThemePreferenceValue {
  return useContext(ThemePreferenceContext);
}

// ---------------------------------------------------------------------------
// Route-driven resolution
// ---------------------------------------------------------------------------

/**
 * Light-mode (paper) routes — marketing surfaces. Anything not in this list
 * defaults to dark (chalkboard) per guideline section 2, unless the route is
 * a preference-themed participant surface (see above).
 *
 * Match rule: exact `/` or pathname starts with one of the prefixes below.
 */
const LIGHT_MODE_PREFIXES: readonly string[] = [
  '/about',
  '/science',
  '/pricing',
  '/research-info',
  '/research-participation',
  '/research-terms',
  '/whitepaper',
  '/protocol',
  '/data-ownership',
  '/privacy-policy',
  '/privacy',
  '/terms-of-service',
  '/become-a-host',
  '/start',
  '/shared-language',
  // Customer reader view (#1087): outward-facing surface, light per the customer-delivery spec.
  '/client',
  // Learning journal (#1162): the same paper surface as the customer view and the host homes.
  '/journal',
  // "Your work" host landing (Host Home canvas): light like every Host Home surface.
  '/host/home',
  // The exercise home and its doors (#1094): the light paper surface end to end.
  '/host/exercises',
  // '/host/sessions' covers the old list (light since part 5b, #1179) and the session
  // console, which already renders light through its own provider (#1171).
  '/host/sessions',
  // The create funnel feeds straight into the light home, so it stops rendering a dark
  // TopNav and help chip over a light body (Kalle 28.8.).
  '/host/create-session',
  // Part 5 of the host-world cleanup (#1179, Kalle 11.9.): the rest of the host world goes
  // light by default, surface by surface. Credits, the daily-study creator and the programme
  // world (home + People) first; the old list and the session console follow.
  '/host/credits',
  '/host/create-study',
  '/host/programmes',
  // The daily-study home (#1179 part 5d).
  '/host/studies',
];

/**
 * Resolve chalkboard mode from a pathname.
 * - `/` → light (homepage is marketing)
 * - any LIGHT_MODE_PREFIXES match → light
 * - everything else → dark (default)
 */
export function resolveModeFromPath(pathname: string): ChalkboardMode {
  if (pathname === '/' || pathname === '') return 'light';
  for (const prefix of LIGHT_MODE_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return 'light';
  }
  return 'dark';
}

/**
 * Auto-provider — resolves the mode from the current route (and, on
 * preference-themed participant surfaces, the stored user preference), sets
 * it for the subtree, and mirrors it as a `dark` class on <html>.
 */
export function ChalkboardModeAutoProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [preference, setPreferenceState] = useState<ChalkboardMode>(readStoredPreference);

  const canToggle = isPreferenceThemedPath(pathname);
  const mode = canToggle ? preference : resolveModeFromPath(pathname);

  // Re-asserted on every route change, not only when the mode value changes (#1171).
  // This provider is the ONE owner of the class: a page that needs a mode its route
  // cannot express (the measurement home) overrides it from its own passive effect,
  // which runs after this layout effect. Pages must never write the class on unmount --
  // that cleanup lands after the next route has already been painted.
  useLayoutEffect(() => {
    document.documentElement.classList.toggle('dark', mode === 'dark');
  }, [mode, pathname]);

  const preferenceValue = useMemo<ThemePreferenceValue>(
    () => ({
      preference,
      canToggle,
      setPreference: (next: ChalkboardMode) => {
        try {
          window.localStorage.setItem(THEME_PREFERENCE_STORAGE_KEY, next);
        } catch {
          // Private browsing / storage denied — apply for this visit only.
        }
        setPreferenceState(next);
      },
    }),
    [preference, canToggle]
  );

  return (
    <ThemePreferenceContext.Provider value={preferenceValue}>
      <ChalkboardModeContext.Provider value={mode}>{children}</ChalkboardModeContext.Provider>
    </ThemePreferenceContext.Provider>
  );
}

/**
 * Resolve the effective mode given a possibly-undefined override.
 * Used by chalkboard components that accept `mode?: ChalkboardMode`.
 */
export function useResolvedMode(override?: ChalkboardMode): ChalkboardMode {
  const ctx = useChalkboardMode();
  return override ?? ctx;
}
